import { afterNextRender, Component, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../../core/api-client.service';
import { I18nService } from '../../core/i18n.service';
import { SiteConfigService } from '../../core/site-config.service';

interface AppointmentPlan {
  key: string;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  featured: boolean;
  badge: string;
  features: string[];
  listingID?: number;
  slug?: string;
}

@Component({
  selector: 'app-appointment-provider-plans',
  imports: [RouterLink],
  template: `
    <section class="plans" [class.compact]="compact()" aria-labelledby="appointment-plans-title">
      <header>
        <div>
          <p>{{ site.text('plansEyebrow', 'İşletmeler için Aymudo Randevu') }}</p>
          <h2 id="appointment-plans-title">{{ site.text('plansTitle', 'Randevularını büyüklüğüne uygun paketle yönet.') }}</h2>
          <span>{{ site.text('plansLead', 'Planını seç, Aymudo hesabınla başvurunu başlat; şube, personel ve hizmetlerini tek panelden yönet.') }}</span>
        </div>
        <div class="billing" aria-label="Faturalama dönemi">
          <button type="button" [class.active]="billing() === 'monthly'" (click)="billing.set('monthly')">Aylık</button>
          <button type="button" [class.active]="billing() === 'annual'" (click)="billing.set('annual')">Yıllık <small>2 ay avantajlı</small></button>
        </div>
      </header>

      <div class="plan-grid">
        @if (!plans().length) { <p class="plan-loading">Paketler ürün kataloğundan yükleniyor…</p> }
        @for (plan of plans(); track plan.key) {
          <article [class.featured]="plan.featured">
            <div class="plan-top">
              <span>{{ plan.badge }}</span>
              <h3>{{ plan.name }}</h3>
              <p>{{ plan.description }}</p>
            </div>
            <div class="price">
              <strong>{{ priceLabel(plan) }}</strong>
              @if (price(plan) > 0) { <small>/ {{ billing() === 'annual' ? 'yıl' : 'ay' }}</small> }
            </div>
            <ul>
              @for (feature of plan.features; track feature) { <li><i>✓</i>{{ feature }}</li> }
            </ul>
            <a
              [routerLink]="i18n.routePath('becomeSeller')"
              [queryParams]="planQuery(plan)"
              (click)="remember(plan)"
            >{{ plan.monthlyPrice === 0 ? 'Ücretsiz işletme hesabı aç' : 'Bu paketle başla' }} <b>→</b></a>
          </article>
        }
      </div>
      <footer>
        <span>Kredi kartı bilgisi vermeden başvurunu başlatabilirsin.</span>
        <a [routerLink]="i18n.routePath('sellerDashboard')">Zaten işletme hesabın var mı? Panele git</a>
      </footer>
    </section>
  `,
  styles: [`
    :host{display:block;min-width:0}.plans{background:linear-gradient(145deg,color-mix(in srgb,var(--accent) 8%,var(--surface)),var(--surface));border:1px solid var(--border);border-radius:36px;display:grid;gap:30px;padding:clamp(24px,5vw,58px)}.plans>header{align-items:end;display:grid;gap:24px;grid-template-columns:minmax(0,1fr) auto}.plans>header>div:first-child{display:grid;gap:10px;max-width:760px}.plans>header p{color:var(--accent);font-size:.72rem;font-weight:950;letter-spacing:.15em;margin:0;text-transform:uppercase}.plans>header h2{color:var(--ink);font-family:var(--font-display,Georgia,serif);font-size:clamp(2.1rem,5vw,4.7rem);letter-spacing:-.065em;line-height:.94;margin:0}.plans>header span{color:var(--muted);line-height:1.65}.billing{background:color-mix(in srgb,var(--ink) 5%,var(--surface));border:1px solid var(--border);border-radius:999px;display:flex;padding:5px}.billing button{background:transparent;border:0;border-radius:999px;color:var(--muted);font:inherit;font-size:.86rem;font-weight:900;padding:10px 13px;white-space:nowrap}.billing button.active{background:var(--ink);color:var(--paper)}.billing small{color:var(--accent);display:block;font-size:.58rem;margin-top:2px}.plan-grid{display:grid;gap:14px;grid-template-columns:repeat(3,minmax(0,1fr))}.plan-grid article{background:var(--surface);border:1px solid var(--border);border-radius:26px;display:grid;gap:20px;min-width:0;padding:24px;position:relative}.plan-grid article.featured{border-color:var(--accent);box-shadow:0 24px 70px color-mix(in srgb,var(--accent) 15%,transparent);transform:translateY(-8px)}.plan-top{display:grid;gap:8px}.plan-top>span{color:var(--accent);font-size:.67rem;font-weight:950;letter-spacing:.1em;text-transform:uppercase}.plan-top h3{color:var(--ink);font-size:1.45rem;margin:0}.plan-top p{color:var(--muted);line-height:1.55;margin:0;min-height:3.2em}.price{align-items:end;border-block:1px solid var(--border);display:flex;gap:7px;padding:18px 0}.price strong{color:var(--ink);font-family:var(--font-display,Georgia,serif);font-size:clamp(2rem,4vw,3.2rem);letter-spacing:-.06em}.price small{color:var(--muted);padding-bottom:7px}.plan-grid ul{display:grid;gap:10px;list-style:none;margin:0;padding:0}.plan-grid li{align-items:center;color:var(--muted);display:flex;font-size:.9rem;gap:9px}.plan-grid li i{align-items:center;background:color-mix(in srgb,var(--accent) 13%,transparent);border-radius:50%;color:var(--accent);display:flex;flex:0 0 23px;font-size:.72rem;font-style:normal;height:23px;justify-content:center}.plan-grid article>a{align-items:center;background:var(--ink);border-radius:999px;color:var(--paper);display:flex;font-weight:950;justify-content:space-between;margin-top:auto;padding:14px 17px;text-decoration:none}.plan-grid article.featured>a{background:var(--accent);color:var(--accent-contrast,var(--paper))}.plans>footer{align-items:center;border-top:1px solid var(--border);display:flex;gap:18px;justify-content:space-between;padding-top:20px}.plans>footer span{color:var(--muted);font-size:.86rem}.plans>footer a{color:var(--accent);font-size:.86rem;font-weight:900}.plans.compact{padding:clamp(22px,4vw,42px)}
    @media(max-width:900px){.plans>header{align-items:start;grid-template-columns:1fr}.billing{justify-self:start}.plan-grid{grid-template-columns:1fr}.plan-grid article.featured{transform:none}.plan-top p{min-height:0}}@media(max-width:560px){.plans{border-radius:26px;padding:22px 17px}.billing{border-radius:18px;display:grid;grid-template-columns:1fr 1fr;width:100%}.billing button{white-space:normal}.plans>footer{align-items:flex-start;display:grid}.plan-grid article{padding:20px}}
  `]
})
export class AppointmentProviderPlansComponent {
  protected readonly site = inject(SiteConfigService);
  protected readonly i18n = inject(I18nService);
  private readonly api = inject(ApiClientService);
  readonly compact = input(false);
  protected readonly billing = signal<'monthly' | 'annual'>('monthly');
  protected readonly plans = signal<AppointmentPlan[]>([]);

  constructor() {
    afterNextRender(() => void this.loadLivePlans());
  }

  protected price(plan: AppointmentPlan): number {
    return this.billing() === 'annual' ? plan.yearlyPrice : plan.monthlyPrice;
  }

  protected priceLabel(plan: AppointmentPlan): string {
    const price = this.price(plan);
    if (price <= 0) return 'Ücretsiz';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency', currency: plan.currency || 'TRY', maximumFractionDigits: 0
    }).format(price);
  }

  protected planQuery(plan: AppointmentPlan): Record<string, string> {
    return { plan: plan.key, billing: this.billing(), source: 'appointment-marketplace' };
  }

  protected remember(plan: AppointmentPlan): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem('aymudo.appointment.selectedPlan', JSON.stringify({
      plan: plan.key,
      billing: this.billing(),
      selectedAt: new Date().toISOString()
    }));
  }

  private async loadLivePlans(): Promise<void> {
    try {
      const response = await firstValueFrom(this.api.post<any>('appointmentMarketplacePlans', {}));
      const raw = response?.data?.items ?? response?.data?.data?.items ?? response?.items;
      if (!Array.isArray(raw) || !raw.length) return;
      const mapped = raw.map((item:unknown,index:number) => {
        const value = item && typeof item === 'object' ? item as Record<string,unknown> : {};
        const features = Array.isArray(value['features']) ? value['features'].map(String).filter(Boolean) : [];
        const trialDays = Math.max(0,Number(value['trialDays'] || 0));
        const featured = Boolean(value['isPopular'] ?? value['featured']);
        return {
          key:String(value['code'] || value['key'] || `plan-${index+1}`),
          name:String(value['name'] || 'Randevu paketi'),
          description:String(value['description'] || ''),
          monthlyPrice:Math.max(0,Number(value['monthlyPrice'] || 0)),
          yearlyPrice:Math.max(0,Number(value['annualPrice'] ?? value['yearlyPrice'] ?? 0)),
          currency:String(value['currency'] || 'TRY'),
          featured,
          badge:featured ? 'En çok tercih edilen' : (trialDays>0 ? `${trialDays} gün ücretsiz dene` : 'Randevu paketi'),
          features,
          listingID:Number(value['listingID'] || 0) || undefined,
          slug:String(value['slug'] || '') || undefined
        } satisfies AppointmentPlan;
      });
      this.plans.set(mapped);
    } catch {
      this.plans.set([]);
    }
  }
}
